"use client";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useMiniCart } from "@/app/context/MiniCartContext";
import { useState, useMemo, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import {
  ChevronDown,
  ShoppingBag,
  User,
  Search,
  X,
  Menu,
  Plus,
  Heart,
} from "lucide-react";
import AuthPopup from "../AuthPopup";

// --- TYPES ---
interface DropdownLink {
  id: number;
  url: string;
  title: string;
  dropDownLinks?: DropdownLink[];
  submenu?: any[];
  label?: string;
  newTab: boolean;
}

interface NavLink {
  id: number;
  url: string;
  newTab: boolean;
  text: string;
  dropDownLinks?: DropdownLink[];
  dropdownLinks?: DropdownLink[];
  button?: any[];
}

interface CartResponse {
  id: number;
  attributes: {
    totalItems?: number;
    lineItems?: {
      data: any[];
    };
  };
}

// --- MAIN NAVBAR COMPONENT ---

export default function Navbar_Layout6({
  links,
  logoUrl,
  logoText,
  cartData,
}: {
  links: Array<NavLink>;
  logoUrl: string | null;
  logoText: string | null;
  button?: any[];
  topBar2?: any;
  cartData: CartResponse;
}) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { open: openMiniCart } = useMiniCart();
  const [searchValue, setSearchValue] = useState("");
  const [activeDropdown, setActiveDropdown] = useState<number | null>(null);
  const [openMobileSubIndex, setOpenMobileSubIndex] = useState<number | null>(
    null,
  );
  const [showAuth, setShowAuth] = useState(false);
  const [openMobileItems, setOpenMobileItems] = useState<number[]>([]);

  const router = useRouter();
  const isLoggedIn = useAuth();

  const handleUserClick = () => {
    if (isLoggedIn) {
      router.push("/account");
    } else {
      setShowAuth(true);
    }
  };

  // --- HELPERS ---
  const getLinks = (item: any): DropdownLink[] => {
    if (item.dropDownLinks && item.dropDownLinks.length > 0)
      return item.dropDownLinks;
    if (item.dropdownLinks && item.dropdownLinks.length > 0)
      return item.dropdownLinks;
    if (item.submenu && item.submenu.length > 0) return item.submenu;
    if (item.children && item.children.length > 0) return item.children;
    if (item.child_items && item.child_items.length > 0)
      return item.child_items;
    return [];
  };

  const getTitle = (item: any): string => {
    return item.title || item.text || item.label || item.name || "";
  };

  const cartQtyCount = useMemo(() => {
    if (typeof cartData?.attributes?.totalItems === "number") {
      return cartData.attributes.totalItems;
    }
    const items = cartData?.attributes?.lineItems?.data ?? [];
    return items.length;
  }, [cartData]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchValue.trim()) {
      router.push(`/search?q=${encodeURIComponent(searchValue.trim())}`);
    } else {
      router.push("/search");
    }
  };

  const closeMenu = () => setMobileMenuOpen(false);

  const handleMouseEnter = (id: number) => {
    setActiveDropdown(id);
  };

  const handleMouseLeave = () => {
    setActiveDropdown(null);
  };

  const handleMobileSubToggle = (index: number) => {
    setOpenMobileSubIndex(openMobileSubIndex === index ? null : index);
  };

  const toggleMobileItem = (index: number) => {
    setOpenMobileItems((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index],
    );
  };

  const shouldShowMegaMenu = (item: NavLink): boolean => {
    const linksData = getLinks(item);
    return linksData.length >= 4;
  };

  // --- MOBILE NESTED DROPDOWN ---
  const MobileNestedDropdown = ({
    links,
    level = 0,
    closeMenu,
  }: {
    links: DropdownLink[];
    level?: number;
    closeMenu: () => void;
  }) => {
    if (!links || links.length === 0) return null;

    return (
      <ul
        className={`flex flex-col gap-2 ${
          level > 0 ? "pl-4 mt-2 border-l-2 border-gray-100 ml-1" : ""
        }`}
      >
        {links.map((link, index) => {
          const nestedLinks = getLinks(link);
          const hasSubMenu = nestedLinks.length > 0;
          const title = getTitle(link);
          const isOpen = openMobileItems.includes(index);

          return (
            <li key={index} className="w-full">
              <div className="flex flex-col w-full">
                {hasSubMenu ? (
                  <button
                    onClick={() => toggleMobileItem(index)}
                    className={`flex justify-between items-center w-full text-left ${
                      level === 0
                        ? "font-bold text-gray-900 uppercase tracking-wide py-3 text-base"
                        : "font-medium text-gray-600 py-2 text-sm hover:text-[var(--secondary)]"
                    }`}
                  >
                    <span>{title}</span>
                    <Plus
                      className={`w-4 h-4 transition-transform duration-300 ${
                        isOpen ? "rotate-45 text-[#ecab2b]" : "text-gray-400"
                      }`}
                    />
                  </button>
                ) : (
                  <Link
                    href={link.url || "#"}
                    onClick={closeMenu}
                    className={`block w-full transition-colors ${
                      level === 0
                        ? "font-bold text-gray-900 uppercase tracking-wide py-3 text-base hover:text-[var(--secondary)]"
                        : "font-medium text-gray-600 py-2 text-sm hover:text-[var(--secondary)]"
                    }`}
                  >
                    {title}
                  </Link>
                )}
              </div>

              {hasSubMenu && (
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    isOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
                  }`}
                >
                  <MobileNestedDropdown
                    links={nestedLinks}
                    level={level + 1}
                    closeMenu={closeMenu}
                  />
                </div>
              )}
            </li>
          );
        })}
      </ul>
    );
  };

  const MegaMenu = ({ links }: { links: DropdownLink[] }) => {
    return (
      <div className="bg-white shadow-xl border-t border-gray-100 py-4 px-8 w-full">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col flex-wrap max-h-[400px]">
            {links.map((column, colIndex) => {
              const columnTitle = getTitle(column);
              const subLinks = getLinks(column);

              return (
                <div
                  key={colIndex}
                  className="flex flex-col space-y-2 pr-10 pb-2"
                >
                  <Link
                    href={column.url || "#"}
                    className="font-bold text-sm tracking-wider text-gray-900 uppercase hover:text-[var(--secondary)] transition-colors"
                  >
                    {columnTitle}
                  </Link>

                  {subLinks.length > 0 && (
                    <ul className="flex flex-col pl-2">
                      {subLinks.map((link, linkIndex) => {
                        const linkTitle = getTitle(link);
                        return (
                          <li key={linkIndex}>
                            <Link
                              href={link.url || "#"}
                              className="text-sm text-gray-600 font-medium hover:text-[var(--secondary)] hover:translate-x-1 transition-all inline-block"
                            >
                              {linkTitle}
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <header className="sticky top-0 left-0 w-full z-40 bg-white text-black shadow-sm py-2">
        <nav className="relative max-w-7xl mx-auto px-4 flex justify-between items-center h-16">
          {/* 1. LOGO */}
          <Link href="/" className="flex-shrink-0 z-50 mr-4">
            {logoUrl ? (
              <img
                className="h-10 w-auto object-contain"
                src={logoUrl}
                alt={logoText || "Logo"}
              />
            ) : (
              <span className="text-2xl font-bold uppercase tracking-tighter text-[#ecab2b]">
                {logoText || "BRAND"}
              </span>
            )}
          </Link>

          {/* 2. DESKTOP NAVIGATION - Centered */}
          {/* Added flex-1 and justify-center to center the links */}
          <ul className="hidden lg:flex flex-1 justify-center gap-6 xl:gap-8 text-[14px] font-medium items-center h-full px-4">
            {links?.map((item, idx) => {
              const linksData = getLinks(item);
              const hasDropdown = linksData.length > 0;
              const isActive = activeDropdown === item.id;
              const isMegaMenu = shouldShowMegaMenu(item);

              return (
                <li
                  key={item.id}
                  className="relative group h-full flex items-center"
                  onMouseEnter={() => handleMouseEnter(item.id)}
                  onMouseLeave={handleMouseLeave}
                >
                  <div className="relative h-full flex items-center">
                    <Link
                      href={item.url}
                      target={item.newTab ? "_blank" : "_self"}
                      className="relative flex items-center gap-1 py-4 text-black font-semibold hover:text-[var(--secondary)] transition-colors"
                    >
                      <span className="uppercase tracking-wide">
                        {item.text}
                      </span>
                      {hasDropdown && (
                        <ChevronDown
                          size={14}
                          className={`transition-transform duration-200 ${
                            isActive ? "rotate-180" : ""
                          }`}
                        />
                      )}
                    </Link>

                    {/* Dropdown Container */}
                    {hasDropdown && isActive && (
                      <>
                        <div className="absolute top-full h-2" />

                        <div
                          className={`absolute top-full z-50 pt-2 ${
                            isMegaMenu
                              ? "-left-10 min-w-[100vw] mx-auto"
                              : "left-1/2 -translate-x-1/2 w-64"
                          }`}
                        >
                          {isMegaMenu ? (
                            <div className="absolute mx-auto">
                              <div className="max-w-7xl mx-auto px-4">
                                <div className="bg-white shadow-xl border mx-auto border-red-500 rounded-lg overflow-hidden">
                                  <MegaMenu links={linksData} />
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="bg-white shadow-lg border rounded-lg py-2 relative">
                              <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-white border-t border-l rotate-45 transform" />

                              <ul className="flex flex-col relative bg-white rounded-lg z-10">
                                {linksData.map((link, linkIndex) => {
                                  const linkTitle = getTitle(link);
                                  const nestedLinks = getLinks(link);
                                  const hasNested = nestedLinks.length > 0;

                                  return (
                                    <li
                                      key={linkIndex}
                                      className="relative group/nested"
                                    >
                                      {hasNested ? (
                                        <>
                                          <div className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 cursor-pointer">
                                            <span className="font-medium text-gray-800">
                                              {linkTitle}
                                            </span>
                                            <ChevronDown
                                              size={12}
                                              className="text-gray-400"
                                            />
                                          </div>
                                          <div className="hidden group-hover/nested:block absolute left-full top-0 ml-1 w-48 bg-white shadow-lg border rounded-lg py-2 z-50">
                                            {nestedLinks.map(
                                              (nested, nestedIndex) => (
                                                <Link
                                                  key={nestedIndex}
                                                  href={nested.url || "#"}
                                                  className="block px-4 py-2 text-sm text-gray-600 hover:text-[var(--secondary)] hover:bg-gray-50"
                                                >
                                                  {getTitle(nested)}
                                                </Link>
                                              ),
                                            )}
                                          </div>
                                        </>
                                      ) : (
                                        <Link
                                          href={link.url || "#"}
                                          className="block px-4 py-3 text-gray-800 hover:text-[var(--secondary)] hover:bg-gray-50 font-medium"
                                        >
                                          {linkTitle}
                                        </Link>
                                      )}
                                    </li>
                                  );
                                })}
                              </ul>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>

          {/* 3. RIGHT SECTION */}
          <div className="flex items-center gap-4">
            {/* SEARCH - VISIBLE BAR */}
            <form
              onSubmit={handleSearchSubmit}
              className="hidden md:flex items-center border border-gray-300 rounded-full px-3 py-1.5 bg-gray-50 focus-within:bg-white focus-within:border-[#ecab2b] transition-colors w-48 xl:w-64"
            >
              <input
                type="text"
                placeholder="What are you looking for?"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                className="flex-1 bg-transparent border-none outline-none text-sm placeholder:text-gray-400 text-gray-700 w-full"
              />
              <button
                type="submit"
                className="text-gray-500 hover:text-[var(--secondary)] ml-1"
                aria-label="Search"
              >
                <Search size={18} />
              </button>
            </form>

            {/* Mobile Search Icon (visible only on small screens if you want, currently hiding form on mobile) */}
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="md:hidden hover:text-[var(--secondary)] transition"
            >
              <Search size={22} />
            </button>

            {/* AUTH */}
            <button
              onClick={handleUserClick}
              className="hidden md:block hover:text-[var(--secondary)] transition"
            >
              <User size={22} />
            </button>

            {/* WISHLIST */}
            <button
              onClick={() => {
                window.open("/account?tab=my-wishlist", "_self");
              }}
              className="hover:text-[var(--secondary)] transition"
            >
              <Heart size={22} />
            </button>

            {/* CART */}
            <button
              onClick={() => openMiniCart()}
              className="hover:text-[var(--secondary)] transition relative"
            >
              <ShoppingBag size={22} />
              {cartQtyCount > 0 && (
                <span className="absolute -top-2 -right-2 text-[10px] h-4 w-4 flex items-center justify-center rounded-full bg-[#ecab2b] text-white font-bold pointer-events-none">
                  {cartQtyCount}
                </span>
              )}
            </button>

            {/* MOBILE TOGGLE */}
            <button
              className="lg:hidden p-1"
              onClick={() => setMobileMenuOpen(true)}
            >
              <Menu size={24} />
            </button>
          </div>
        </nav>
      </header>

      {/* --- AUTH POPUP --- */}
      <AuthPopup
        isOpen={showAuth}
        onClose={() => setShowAuth(false)}
        onLoginSuccess={() => {
          setShowAuth(false);
          router.push("/account");
        }}
      />

      {/* --- MOBILE SIDEBAR MENU --- */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
            onClick={closeMenu}
          />
          <div className="fixed inset-y-0 right-0 w-[85%] max-w-sm bg-white shadow-2xl flex flex-col transform transition-transform duration-300 ease-in-out animate-in slide-in-from-right">
            <div className="flex items-center justify-between p-4 border-b">
              <span className="font-bold text-lg uppercase text-gray-800">
                Menu
              </span>
              <button
                onClick={closeMenu}
                className="p-2 hover:bg-gray-100 rounded-full text-gray-500"
              >
                <X size={24} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5">
              <nav className="flex flex-col space-y-4">
                {links?.map((item: any, idx: number) => {
                  const linksData = getLinks(item);
                  const hasDropdown = linksData.length > 0;
                  const isExpanded = openMobileSubIndex === idx;

                  return (
                    <div
                      key={item.id}
                      className="border-b border-gray-200 last:border-0 pb-4"
                    >
                      {hasDropdown ? (
                        <>
                          <button
                            onClick={() => handleMobileSubToggle(idx)}
                            className={`flex items-center justify-between w-full text-left text-base uppercase tracking-wide font-medium transition-colors duration-300 py-2 ${
                              isExpanded
                                ? "text-[#ecab2b]"
                                : "text-gray-900 hover:text-gray-600"
                            }`}
                          >
                            {item.text}
                            <ChevronDown
                              className={`w-5 h-5 transition-transform duration-300 ${
                                isExpanded ? "rotate-180" : ""
                              }`}
                            />
                          </button>

                          {/* Level 1 Container */}
                          <div
                            className={`overflow-hidden transition-all duration-300 ease-in-out ${
                              isExpanded
                                ? "max-h-[1000px] opacity-100 mt-2"
                                : "max-h-0 opacity-0"
                            }`}
                          >
                            <div className="pl-4 space-y-3 pb-2">
                              <MobileNestedDropdown
                                links={linksData}
                                level={1}
                                closeMenu={closeMenu}
                              />
                            </div>
                          </div>
                        </>
                      ) : (
                        <Link
                          href={item.url}
                          onClick={closeMenu}
                          className="block text-base uppercase tracking-wide font-medium text-gray-900 hover:text-gray-600 py-2"
                        >
                          {item.text}
                        </Link>
                      )}
                    </div>
                  );
                })}
              </nav>
            </div>
            <div className="p-4 bg-gray-50 border-t space-y-4">
              <form
                onSubmit={(e) => {
                  handleSearchSubmit(e);
                  closeMenu();
                }}
                className="relative"
              >
                <input
                  type="text"
                  placeholder="What are you looking for?"
                  value={searchValue}
                  className="w-full pl-10 pr-4 py-2 rounded-full border border-gray-300 focus:border-[#ecab2b] focus:ring-1 focus:ring-[#ecab2b] outline-none text-sm"
                  onChange={(e) => setSearchValue(e.target.value)}
                />
                <Search className="absolute left-3 top-2.5 text-gray-400 w-4 h-4" />
              </form>
              <button
                onClick={() => {
                  if (isLoggedIn) {
                    router.push("/account");
                  } else {
                    setShowAuth(true);
                  }
                  closeMenu();
                }}
                className="flex items-center gap-2 w-full px-4 py-3 bg-black text-white rounded-md"
              >
                <User size={16} />
                <span>Sign In / Register</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
